import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { Modal } from "bootstrap";
import { checkFiles, generateRandomNumber } from "../helpers"

const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads";

function formatEta(bytesRemaining, bytesPerSec) {
  if (!bytesPerSec || bytesPerSec <= 0) return "";
  const seconds = bytesRemaining / bytesPerSec;
  if (!isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return `${Math.ceil(seconds)}s left`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} mins left`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.ceil((seconds - hours * 3600) / 60);
  return mins === 0 ? `${hours}h left` : `${hours}h ${mins}m left`;
}

export default class extends Controller {
  static targets = ["fileUploadContainer", "bgUploadFile", "submitButton", "compactDropzone", "compactRemainingText"];

  connect() {
    this.pendingUploads = 0;
    // Counter tracks nested dragenter/dragleave so the active state only clears
    // when the file leaves the page entirely, not when it crosses a child element.
    this._fileDragCounter = 0;

    this._handleDocDragEnter = (event) => {
      if (!this._isFileDrag(event)) return;
      event.preventDefault();
      this._fileDragCounter += 1;
      this._setDragOver(true);
    };

    this._handleDocDragOver = (event) => {
      if (!this._isFileDrag(event)) return;
      event.preventDefault();
      const overDropzone = this.hasBgUploadFileTarget && this.bgUploadFileTarget.contains(event.target);
      event.dataTransfer.dropEffect = overDropzone ? "copy" : "none";
    };

    this._handleDocDragLeave = (event) => {
      if (!this._isFileDrag(event)) return;
      this._fileDragCounter = Math.max(0, this._fileDragCounter - 1);
      if (this._fileDragCounter === 0) this._setDragOver(false);
    };

    this._handleDocDrop = (event) => {
      if (!this._isFileDrag(event)) return;
      event.preventDefault();
      this._fileDragCounter = 0;
      this._setDragOver(false);
    };

    document.addEventListener("dragenter", this._handleDocDragEnter);
    document.addEventListener("dragover", this._handleDocDragOver);
    document.addEventListener("dragleave", this._handleDocDragLeave);
    document.addEventListener("drop", this._handleDocDrop);

    // Navigation guard while uploads in flight.
    this._pendingNavigationUrl = null;

    this._handleBeforeVisit = (event) => {
      if (this.pendingUploads <= 0) return;
      event.preventDefault();
      this._pendingNavigationUrl = event.detail?.url || null;
      this._pendingNavigationAction = "visit";
      this._showNavigationGuardModal();
    };

    this._handleBeforeUnload = (event) => {
      if (this.pendingUploads <= 0) return;
      event.preventDefault();
      event.returnValue = "";
    };

    this._handleLeavePageClick = (event) => {
      if (event.target.closest("#upload-nav-guard-leave-btn")) {
        this._confirmNavigationGuard();
      }
    };

    // Intercept any back-style nav (new-referral header uses handleBackClick,
    // referral-details header uses the back-navigation controller) and any
    // existing unsaved-changes confirmation triggers, so the upload guard wins
    // when uploads are still in flight.
    this._bypassNavigationGuard = false;
    this._handleBackClickCapture = (event) => {
      if (this._bypassNavigationGuard) return;
      if (this.pendingUploads <= 0) return;
      const trigger = event.target.closest(
        '[data-controller~="back-navigation"], [data-action*="handleBackClick"], [data-action*="confirmation-modal#showModal"]'
      );
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this._navigationGuardTrigger = trigger;
      this._pendingNavigationUrl = trigger.dataset.fallbackUrl || null;
      // Back-style triggers can navigate via history; confirmation-modal triggers
      // hand off to other flows (form discards, etc.) — re-fire the original click.
      const isBackStyle = trigger.matches('[data-controller~="back-navigation"], [data-action*="handleBackClick"]');
      this._pendingNavigationAction = isBackStyle ? "back" : "reclick";
      this._showNavigationGuardModal();
    };

    document.addEventListener("turbo:before-visit", this._handleBeforeVisit);
    window.addEventListener("beforeunload", this._handleBeforeUnload);
    document.addEventListener("click", this._handleLeavePageClick);
    document.addEventListener("click", this._handleBackClickCapture, true);
  }

  disconnect() {
    document.removeEventListener("dragenter", this._handleDocDragEnter);
    document.removeEventListener("dragover", this._handleDocDragOver);
    document.removeEventListener("dragleave", this._handleDocDragLeave);
    document.removeEventListener("drop", this._handleDocDrop);
    document.removeEventListener("turbo:before-visit", this._handleBeforeVisit);
    window.removeEventListener("beforeunload", this._handleBeforeUnload);
    document.removeEventListener("click", this._handleLeavePageClick);
    document.removeEventListener("click", this._handleBackClickCapture, true);
  }

  _showNavigationGuardModal() {
    const modalEl = document.getElementById("modal_upload_navigation_guard");
    if (!modalEl) return;
    Modal.getOrCreateInstance(modalEl).show();
  }

  _confirmNavigationGuard() {
    const url = this._pendingNavigationUrl;
    const action = this._pendingNavigationAction;
    const trigger = this._navigationGuardTrigger;
    this._pendingNavigationUrl = null;
    this._pendingNavigationAction = null;
    this._navigationGuardTrigger = null;
    // User confirmed they're abandoning the uploads — zero out pending so the
    // next turbo:before-visit doesn't re-prompt.
    this.pendingUploads = 0;
    this.updateSubmitButtonState();

    const modalEl = document.getElementById("modal_upload_navigation_guard");
    if (modalEl) Modal.getInstance(modalEl)?.hide();

    if (action === "back") {
      if (window.history.length > 1) {
        window.history.back();
      } else if (url && typeof Turbo !== "undefined") {
        Turbo.visit(url);
      }
    } else if (action === "reclick" && trigger) {
      // Re-fire the click that we intercepted, this time letting it through
      // so the existing confirmation/navigation flow takes over.
      this._bypassNavigationGuard = true;
      trigger.click();
      setTimeout(() => { this._bypassNavigationGuard = false; }, 0);
    } else if (url && typeof Turbo !== "undefined") {
      Turbo.visit(url);
    }
  }

  _isFileDrag(event) {
    return event.dataTransfer?.types?.includes("Files");
  }

  _setDragOver(active) {
    if (!this.hasBgUploadFileTarget) return;
    this.bgUploadFileTarget.classList.toggle("upload-dropzone--drag-over", active);
  }

  toggleInitialUploadFileBlock() {
    if (!this.hasBgUploadFileTarget || !this.fileUploadContainerTarget) return;

    const fileCount = this.fileUploadContainerTarget.querySelectorAll('.file-wrapper').length;
    const maxFiles = parseInt(this.bgUploadFileTarget.dataset.maxFiles, 10) || 5;
    const remaining = Math.max(maxFiles - fileCount, 0);
    const compactMode = this.bgUploadFileTarget.dataset.compactMode === 'true';

    if (compactMode) {
      this.bgUploadFileTarget.classList.toggle('upload-dropzone--has-files', fileCount > 0);

      if (this.hasCompactDropzoneTarget) {
        this.compactDropzoneTarget.style.display = remaining > 0 ? '' : 'none';
      }

      if (this.hasCompactRemainingTextTarget) {
        const noun = remaining === 1 ? 'file' : 'files';
        this.compactRemainingTextTarget.textContent = `up to ${remaining} more ${noun}`;
      }
    } else {
      this.bgUploadFileTarget.classList.toggle('d-none', fileCount > 0);
    }
  }

  handleClickFileUpload(e) {
    const fileWrapperEl = e.currentTarget.closest('.file-wrapper');
    const fileNameEditable = fileWrapperEl.querySelector(".file-name-editable");
    // When the click lands on the editable filename itself, let the browser
    // handle caret placement and text selection natively. Otherwise our
    // collapse-to-end below would wipe a highlight the user just made.
    if (fileNameEditable.contains(e.target)) return;
    // If the user has an active text selection that began inside the filename
    // (e.g. they drag-selected and released the mouse outside the field), don't
    // collapse it — let them keep the highlight.
    const currentSelection = window.getSelection();
    if (currentSelection && !currentSelection.isCollapsed && fileNameEditable.contains(currentSelection.anchorNode)) return;
    fileNameEditable.focus();
    const value = fileNameEditable.innerText;
    fileNameEditable.innerText = '';
    fileNameEditable.innerText = value;
    const range = document.createRange();
    range.selectNodeContents(fileNameEditable);
    range.collapse(false);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  uploadFiles(e){
    this.processFiles(e.target.files);
    e.target.value = '';
  }

  handleDrop(event) {
    event.preventDefault();
    if (this.hasBgUploadFileTarget) {
      this.bgUploadFileTarget.classList.remove("upload-dropzone--drag-over");
    }
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.processFiles(droppedFiles);
    }
  }

  processFiles(files){
    const dropzoneMaxMb = this.hasBgUploadFileTarget ? this.bgUploadFileTarget.dataset.maxSizeMb : null;
    const [selectedFiles] = checkFiles(files, { maxSizeMb: dropzoneMaxMb });
    const selectingFilesAmount = files.length;
    const existingFileAmount = this.fileUploadContainerTarget.querySelectorAll('.file-wrapper').length;

    if (existingFileAmount + selectingFilesAmount > 5) {
      alert("You can only upload a maximum of 5 files")
      return;
    }

    selectedFiles.forEach((file, index) => {
      const randomKey = generateRandomNumber() + index;
      const fileExtName = `.${file.name.split('.').pop()}`;
      const fileBaseName = file.name.split('.').slice(0, -1).join('.');

      const fileWrapperEl = $('<div class="file-wrapper file-wrapper--uploading" data-action="click->file-upload#handleClickFileUpload"></div>');

      const $icon = $('<i class="fa-regular fa-file-lines file-wrapper__icon"></i>');

      const $body = $('<div class="file-wrapper__body"></div>');

      const $label = $('<div class="file-wrapper__label"></div>');
      const $prefix = $('<span class="file-wrapper__label-prefix">Uploading file:</span>');
      // Only the base name is editable; the extension is appended by renameFile.
      // Rendering the full name here would cause the extension to be duplicated
      // (e.g. "file_edit.png.png").
      const $nameGroup = $('<span class="file-wrapper__name-group"></span>');
      const $filename = $(`<span class="file-wrapper__filename file-name-editable" contenteditable="true" data-action="input->file-upload#renameFile keydown->file-upload#handleFilenameKeydown" data-random-key="${randomKey}" data-file-base-name="${fileBaseName}" data-file-ext-name="${fileExtName}">${fileBaseName}</span>`);
      const $ext = $(`<span class="file-wrapper__ext">${fileExtName}</span>`);
      $nameGroup.append($filename).append($ext);
      $label.append($prefix).append($nameGroup);

      const $progressRow = $('<div class="file-wrapper__progress-row"></div>');
      const $percent = $('<span class="file-wrapper__percent">0%</span>');
      const $bar = $('<div class="file-wrapper__bar"><div class="file-wrapper__bar-fill"></div></div>');
      const $eta = $('<span class="file-wrapper__eta"></span>');
      $progressRow.append($percent).append($bar).append($eta);

      $body.append($label).append($progressRow);

      const $editBtn = $('<i class="fa-regular fa-pencil file-wrapper__action file-wrapper__action--edit"></i>');
      const $trashBtn = $('<i class="fa-regular fa-trash file-wrapper__action" data-action="click->file-upload#removeFileUpload"></i>');

      fileWrapperEl.append($icon).append($body).append($editBtn).append($trashBtn);

      const hiddenField = document.createElement('input');
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("class", "d-none file-upload-input");
      hiddenField.name = `file_upload[${randomKey}][file]`;
      hiddenField.disabled = true;
      fileWrapperEl.append(hiddenField);

      const $nameField = $(`<input type="hidden" name="file_upload[${randomKey}][name]" value="${file.name}" class="file-upload-name-field" />`);
      fileWrapperEl.append($nameField);

      $(this.fileUploadContainerTarget).append(fileWrapperEl);

      this.startDirectUpload(file, fileWrapperEl[0], hiddenField, {
        percentEl: $percent[0],
        barFillEl: $bar.find('.file-wrapper__bar-fill')[0],
        etaEl: $eta[0]
      });
    })

    this.toggleInitialUploadFileBlock();
    // Lets sibling controllers on the same element (e.g. message-form) react
    // to attachments being added/completed/removed.
    this.dispatch("changed");
  }

  startDirectUpload(file, fileWrapperEl, hiddenField, progressUI) {
    this.pendingUploads += 1;
    this.updateSubmitButtonState();

    const uploadUrl = this.element.dataset.directUploadUrl || DIRECT_UPLOAD_URL;

    // Track average upload speed via EMA so ETA doesn't jitter with each TCP burst.
    let lastTime = null;
    let lastLoaded = 0;
    let smoothedBytesPerSec = 0;
    const EMA_ALPHA = 0.3;

    const upload = new DirectUpload(file, uploadUrl, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return;
          const now = Date.now();
          if (lastTime !== null) {
            const elapsedSec = (now - lastTime) / 1000;
            const bytesDelta = event.loaded - lastLoaded;
            if (elapsedSec > 0 && bytesDelta >= 0) {
              const instant = bytesDelta / elapsedSec;
              smoothedBytesPerSec = smoothedBytesPerSec === 0
                ? instant
                : EMA_ALPHA * instant + (1 - EMA_ALPHA) * smoothedBytesPerSec;
            }
          }
          lastTime = now;
          lastLoaded = event.loaded;

          const pct = Math.round((event.loaded / event.total) * 100);
          if (progressUI?.percentEl) progressUI.percentEl.textContent = `${pct}%`;
          if (progressUI?.barFillEl) progressUI.barFillEl.style.width = `${pct}%`;
          if (progressUI?.etaEl) {
            const bytesRemaining = event.total - event.loaded;
            progressUI.etaEl.textContent = formatEta(bytesRemaining, smoothedBytesPerSec);
          }
        });
      }
    });

    upload.create((error, blob) => {
      this.pendingUploads = Math.max(0, this.pendingUploads - 1);

      if (error) {
        console.error("Direct upload failed", error);
        fileWrapperEl.classList.remove('file-wrapper--uploading');
        fileWrapperEl.classList.add('file-wrapper--failed');
      } else {
        hiddenField.value = blob.signed_id;
        hiddenField.disabled = false;
        fileWrapperEl.classList.remove('file-wrapper--uploading');
        fileWrapperEl.classList.add('file-wrapper--completed');
      }

      this.updateSubmitButtonState();
      this.dispatch("changed");
    });
  }

  updateSubmitButtonState() {
    let buttons = this.hasSubmitButtonTarget ? this.submitButtonTargets : [];
    if (buttons.length === 0) {
      const legacy = document.querySelector('#newReferralFormSubmitButton');
      if (legacy) buttons = [legacy];
    }

    const pending = this.pendingUploads > 0;
    buttons.forEach((btn) => {
      btn.disabled = pending;
      if (pending) {
        btn.dataset.uploadsPending = "true";
      } else {
        delete btn.dataset.uploadsPending;
      }
    });
  }

  // Pressing Enter while editing a filename should commit the edit and blur the
  // field rather than inserting a newline into the contenteditable element.
  handleFilenameKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }

  renameFile(e){
    const fileBaseName = e.currentTarget.innerText;
    const fileExtName = e.currentTarget.dataset.fileExtName;
    const newFileName = `${fileBaseName}${fileExtName}`;

    const fileUploadId = e.currentTarget.dataset.fileUploadId;
    const randomKey = e.currentTarget.dataset.randomKey;

    if (randomKey && !fileUploadId) {
      const fileWrapperEl = e.currentTarget.closest('.file-wrapper');
      const nameField = fileWrapperEl.querySelector('.file-upload-name-field');
      if (nameField) nameField.value = newFileName;
    } else if (fileUploadId) {
      const randomNumber = randomKey || generateRandomNumber();
      const $fileUploadNewFileNameInput = $(`<input type="text" name="file_upload[${randomNumber}][new_file_name]" value="${newFileName}" class="d-none file-upload-new-file-name-${fileUploadId}" />`);
      const $fileUploadIdInput = $(`<input type="text" name="file_upload[${randomNumber}][id]" value="${fileUploadId}" class="d-none file-upload-id-${fileUploadId}" />`);
      $(this.fileUploadContainerTarget).append($fileUploadNewFileNameInput).append($fileUploadIdInput);
    }
  }

  resetFileName(e) {
    const fileWrapperEl = e.currentTarget.closest('.file-wrapper');
    const fileNameEl = fileWrapperEl.querySelector('.file-name .file-base-name');
    fileNameEl.innerText = fileNameEl.dataset.fileBaseName;

    const fileUploadId = e.currentTarget.dataset.fileUploadId;
    if (fileUploadId) {
      const fileUploadNewFileNameInput = document.querySelector(`input.file-upload-new-file-name-${fileUploadId}`);
      const fileUploadIdInput = document.querySelector(`input.file-upload-id-${fileUploadId}`);

      if (fileUploadNewFileNameInput && fileUploadIdInput) {
        fileUploadNewFileNameInput.remove();
        fileUploadIdInput.remove();
      }
    } else {
      const nameField = fileWrapperEl.querySelector('.file-upload-name-field');
      const originalName = e.currentTarget.dataset.originName;
      if (nameField && originalName) nameField.value = originalName;
    }
  }

  removeFileUpload(event) {
    if (event.target.dataset.fileUploadId) {
      const fileUploadDestroyInput = $(`<input type="text" name="file_upload[${event.target.dataset.fileUploadId}][_destroy]" value="1" class="d-none" />`);
      const fileUploadIdInput = $(`<input type="text" name="file_upload[${event.target.dataset.fileUploadId}][id]" value="${event.target.dataset.fileUploadId}" class="d-none" />`);
      $(this.fileUploadContainerTarget).append(fileUploadDestroyInput).append(fileUploadIdInput);
    }

    $(event.target).closest('.file-wrapper').remove();

    this.toggleInitialUploadFileBlock();
    this.updateSubmitButtonState();
    this.dispatch("changed");
  }
}
